/**
 * With introduction of SSR we have to run some scripts only on client side.
 * Code relying on `window` or `document` will fail on server.
 */
export const isClientSide = typeof window !== 'undefined'

/**
 * Loads from the SherlockContainer or gives a given defaultValue.
 * Shortcut to avoid window.hasOwnProperty test.
 */
export const getFromContainer = function (key, defaultValue) {
  if (isClientSide) {
    if (!window.hasOwnProperty('SherlockContainer')) {
      return defaultValue
    }

    if (!window.SherlockContainer.hasOwnProperty(key)) {
      return defaultValue
    }

    return window.SherlockContainer[key]
  }
}

export const locale = getFromContainer('locale', 'de-de')

/**
 * Get environment related constants from the backend.
 *
 * Check window.SherlockContainer.environment for available options.
 */
const envVars = getFromContainer('environment', {})
export const env = function (key) {
  if (envVars.hasOwnProperty(key)) {
    return envVars[key]
  }
  return key
}

/**
 * Shorthand link to static images.
 */
export const staticAsset = function (path) {
  if (!path.startsWith('/')) {
    path = '/' + path
  }
  return env('CDN_S') + path
}

export const baseURL = function (path) {
  if (!path.startsWith('/-')) {
    path = '/-' + path
  }
  return env('BASE_URL') + path
}

/**
 * In some places we're using toLocaleString, in some others we use
 * the following function. Maybe deciding on which one stays and which
 * one goes is a good idea
 */
export function formatNumber(number, decimals, dec_point, thousands_sep) {
  let n = !isFinite(+number) ? 0 : +number,
    prec = !isFinite(+decimals) ? 0 : Math.abs(decimals),
    sep = typeof thousands_sep === 'undefined' ? '.' : thousands_sep,
    dec = typeof dec_point === 'undefined' ? ',' : dec_point,
    toFixedFix = (n, prec) => {
      // Fix for IE parseFloat(0.55).toFixed(0) = 0;
      const k = Math.pow(10, prec)
      return (n * k) / k
    },
    s = (prec ? toFixedFix(n, prec) : n).toString().split('.')

  if (s[0].length > 3) {
    s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep)
  }
  if ((s[1] || '').length < prec) {
    s[1] = s[1] || ''
    s[1] += new Array(prec - s[1].length + 1).join('0')
  }
  return s.join(dec)
}

export const supportsLocalStorage = () => {
  let mod = 'f'
  try {
    localStorage.setItem(mod, mod)
    localStorage.removeItem(mod)

    return true
  } catch (e) {
    return false
  }
}

export const getTestParam = testKey => {
  if (supportsLocalStorage()) {
    try {
      const LS = JSON.parse(localStorage.getItem('ab-testing') || '{}')

      return LS[testKey] || 'A'
    } catch (e) {}
  } else {
    return 'A'
  }
}

export const getUUIDv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export const isDebugMode =
  isClientSide && /debug=true/.test(window.location.href)

export const getStepValue = (step, fieldName) => {
  return step.values?.find(value => value.name === fieldName)?.value
}

export const isHouseRelatedType = type => /house/.test(type)
export const isVendorRelatedType = type => /vendor/.test(type)
export const isFinanceRelatedType = type => /finance/.test(type)
export const isOutfitterRelatedType = type => /outfitter/.test(type)

export const replaceDotWithComma = number =>
  number?.toString().replace(/\./, ',')

export const formatRating = rating =>
  rating % 1 === 0 && rating !== 0 ? `${rating},0` : replaceDotWithComma(rating)
